import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { IArtistAlbums, IArtistResponse } from '#/types/API/artists';
import { header2ClassName, spotifyApiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import tinyColor from 'tinycolor2';
import Vibrant from 'node-vibrant';
import useProfile from '#/hooks/useProfile';
import PlayButton from '#/components/Form/PlayButton/PlayButton';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import useViewport from '#/hooks/useViewport';
import { capitalize } from 'lodash';
import { ROUTE_NAMES } from '#/router/routes';
import LoadingPage from '#/components/Layout/LoadingPage/LoadingPage';
import TopItemPreview from '#/components/Layout/TopItemPreview/TopItemPreview';
import {
  createActionAlbumReset,
  createActionAlbumSetAlbum,
  createActionAlbumSetErrorDetails,
  createActionAlbumSetIsFollowed,
  createActionAlbumSetOtherAlbums,
  createActionAlbumSetTrackIsFollowed,
  createActionAlbumStartLoading,
  createActionAlbumStopLoading,
} from '#/redux/actions/album';
import TrackTableHead from '#/components/Layout/TracksTableHead/TracksTableHead';
import { IAlbumResponse } from '#/types/API/albums';
import msToReadableLongTime from '#/utils/msToReadableLongTime';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import useDeviceId from '#/hooks/useDeviceId';
import {
  emptyContainsResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const albumSelector = (state: TRootState) => state.album;

type TColor = [number, number, number];

const Album = () => {
  const navigate = useNavigate();

  const imageReference = useRef<HTMLImageElement>(null);

  const profile = useProfile();

  const { viewportWidth } = useViewport();

  const [dominantColor, setDominantColor] = useState<undefined | TColor>();

  const albumSlice = useSelector(albumSelector);

  const { album, other_albums, _error, _loading } = albumSlice;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const { id } = useParams();

  const otherAlbumsLimit = 6;

  const { retrieveDeviceId } = useDeviceId();

  const handleClickPlay = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerPlay',
      '',
      {
        context_uri: album?.uri,
      },
      {
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant reproduce');
    }
  };

  useEffect(() => {
    const getAlbumDetails = async () => {
      dispatch(createActionAlbumStartLoading());
      if (id) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responseAlbum = await apiMannager.getEntity<IAlbumResponse>(
          'albums',
          id
        );

        if ('error' in responseAlbum) {
          // TODO: handle error
          dispatch(createActionAlbumSetErrorDetails(true));
        } else {
          const albumData = responseAlbum.data;

          const responseOtherAlbums =
            await apiMannager.getEntity<IArtistAlbums>(
              'artists',
              `${albumData.artists[0].id}/albums`,
              { limit: otherAlbumsLimit }
            );

          const responseArtist = await apiMannager.getEntity<IArtistResponse>(
            'artists',
            albumData.artists[0].id
          );

          const responseIsFollowed = await apiMannager.getAllEntities<
            boolean[]
          >('libraryContains', {
            uris: urisParameter('album', id),
          });
          if (
            'error' in responseIsFollowed ||
            'error' in responseArtist ||
            'error' in responseOtherAlbums
          ) {
            // TODO: handle error
            dispatch(createActionAlbumSetErrorDetails(true));
          } else {
            const otherAlbumsData = responseOtherAlbums.data.items;

            const isFavorite = responseIsFollowed.data[0];

            const albumTrackIds = albumData.tracks.items.map(
              (track) => track.id
            );
            const responseFavTracks =
              albumTrackIds.length > 0
                ? await apiMannager.getAllEntities<boolean[]>(
                    'libraryContains',
                    {
                      uris: urisParameter('track', albumTrackIds),
                    }
                  )
                : await emptyContainsResponse();
            if ('error' in responseFavTracks) {
              dispatch(createActionAlbumSetErrorDetails(true));
            } else {
              const tracksWithFavorite = albumData.tracks.items.map(
                (track, index) => ({
                  ...track,
                  favorite: responseFavTracks.data[index],
                })
              );

              albumData.artists[0] = responseArtist.data;

              dispatch(
                createActionAlbumSetAlbum({
                  ...albumData,
                  favorite: isFavorite,
                  tracks: { ...albumData.tracks, items: tracksWithFavorite },
                })
              );

              dispatch(createActionAlbumSetOtherAlbums(otherAlbumsData));

              dispatch(createActionAlbumStopLoading());
            }
          }
        }
      }
    };

    getAlbumDetails();
  }, [dispatch, id, profile?.country, protectedSpotify]);

  useEffect(() => {
    if (album) {
      Vibrant.from(album.images[0].url)
        .getPalette()
        .then((palette) => setDominantColor(palette.Vibrant?.getRgb()));
    }
  }, [album]);

  useEffect(() => {
    return () => {
      dispatch(createActionAlbumReset());
    };
  }, [dispatch]);

  const handleClickFavorite = async () => {
    const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);
    if (album?.favorite) {
      const responseRemoveFromFavorites = await apiMannager.deleteEntity(
        'library',
        '',
        {
          uris: urisParameter('album', album.id),
        }
      );

      if ('error' in responseRemoveFromFavorites) {
        //TODO: handle error
      } else {
        dispatch(
          createActionAlbumSetIsFollowed({
            follow: false,
          })
        );
      }
    } else {
      const responseAddToFavorites = await apiMannager.replaceEntity(
        'library',
        '',
        undefined,
        {
          uris: urisParameter('album', album?.id ?? ''),
        }
      );

      if ('error' in responseAddToFavorites) {
        //TODO: handle error
      } else {
        //
        dispatch(
          createActionAlbumSetIsFollowed({
            follow: true,
          })
        );
      }
    }
  };

  const getBannerStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const color = tinyColor(`rgb(${dominantColor.join(', ')})`);
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(22);
      style.backgroundImage = `linear-gradient(${color.toRgbString()}, ${darkerDominant.toRgbString()})`;
    }
    return style;
  };

  const getBodyStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(28);
      style.backgroundImage = `linear-gradient(${darkerDominant.toRgbString()}, transparent 240px)`;
    }
    return style;
  };

  return (
    <>
      {_loading || _error ? (
        <LoadingPage />
      ) : (
        <div className="mt-theme-top-bar-negative-height">
          <div className="pt-theme-top-bar-height" style={getBannerStyle()}>
            <div className="flex gap-6 pt-9 p-theme-page-padding">
              <div className=" w-[192px] 2xl:w-[232px]">
                <div className="w-full aspect-square overflow-hidden shadow-2xl shadow-theme-shadow">
                  <img
                    className="  h-full w-full object-cover"
                    ref={imageReference}
                    src={album?.images[0].url}
                    alt=""
                  />
                </div>
              </div>
              <div className=" flex-1 flex flex-col justify-end pb-1">
                <span className="font-bold text-sm">
                  {capitalize(album?.album_type)}
                </span>
                <Header
                  type="h1"
                  className="text-6xl mb-9 font-bold tracking-tighter"
                >
                  {album?.name}
                </Header>
                <div className="flex gap-1 text-sm items-center">
                  <div className="w-6 aspect-square rounded-full overflow-hidden">
                    <img
                      className="object-cover"
                      src={album?.artists[0].images[2].url}
                      alt=""
                    />
                  </div>
                  <Link to={`${ROUTE_NAMES.ARTIST}/${album?.artists[0].id}`}>
                    <span className="hover:underline font-bold">
                      {album?.artists[0].name}
                    </span>
                  </Link>
                  •<span>{album?.release_date.slice(0, 4)}</span>•
                  <span>{album?.total_tracks} songs,</span>
                  <span className=" text-theme-light-gray">
                    {msToReadableLongTime(
                      album
                        ? album.tracks.items
                            .map((item) => item.duration_ms)
                            .reduce((sum, current) => sum + current)
                        : 0
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="min-h-full p-theme-page-padding"
            style={getBodyStyle()}
          >
            <div className="flex flex-row items-center gap-8 mb-theme-page-padding">
              <PlayButton onClick={handleClickPlay} />
              <span
                onClick={handleClickFavorite}
                className={`material-icons text-[32px] cursor-default ${
                  album?.favorite
                    ? 'text-theme-green'
                    : 'text-theme-light-gray hover:text-theme-white'
                } `}
                //onClick={onClickFavorite}
              >
                {album?.favorite ? 'favorite' : 'favorite_border'}
              </span>
            </div>
            <div className=" mb-10">
              <TrackTableHead />

              {album?.tracks.items?.map((track, index) => {
                const handleClickFavoriteTrack = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (track.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('track', track.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionAlbumSetTrackIsFollowed({
                          track: track.id,
                          follow: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('track', track.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionAlbumSetTrackIsFollowed({
                          track: track.id,
                          follow: true,
                        })
                      );
                    }
                  }
                };

                return (
                  <TrackListItem
                    id={track.id}
                    key={track.id}
                    name={track.name}
                    duration={track.duration_ms}
                    explicit={track.explicit}
                    number={index + 1}
                    followed={track.favorite ?? false}
                    artists={track.artists.map((artist) => ({
                      name: artist.name,
                      id: artist.id,
                    }))}
                    onFavorite={handleClickFavoriteTrack}
                    uri={track.uri}
                    contextUri={album.uri}
                  />
                );
              })}
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Header type="h2" className={header2ClassName}>
                  More by {album?.artists[0].name}
                </Header>
                <Link
                  className="link"
                  to={`${ROUTE_NAMES.ARTIST}/${album?.artists[0].id}/discography`}
                >
                  Show all
                </Link>
              </div>
              <PreviewLayout>
                {other_albums?.map((album, index) => {
                  if (index >= getItemsPerBreakpoint(viewportWidth)) {
                    return;
                  }

                  const handleOnClick = () =>
                    navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                  return (
                    <TopItemPreview
                      key={album.id}
                      name={album.name}
                      image={album.images[0].url}
                      releaseYear={Number(album.release_date.slice(0, 4))}
                      onClick={handleOnClick}
                      uri={album.uri}
                    />
                  );
                })}
              </PreviewLayout>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Album;
